// client/src/components/pacotesCliente/PacotesCliente.js
import React, { useState, useMemo, Fragment } from 'react';
import { usePacotesCliente, useMutatePacotesCliente } from '../../hooks/usePacotesCliente';
import { useClientes } from '../../hooks/useClientes';
import { useMe } from '../../hooks/useAuth';
import PacoteClienteForm from './PacoteClienteForm';
import PacoteClienteRow from './PacoteClienteRow';
import FiltroPacotesCliente from './FiltroPacotesCliente';
import { PlusCircle, Filter as FilterIcon, Loader2, XCircle, Archive, SquarePen, Trash2 } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const PacotesCliente = () => {
    const [showForm, setShowForm] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [pacoteEmEdicao, setPacoteEmEdicao] = useState(null);
    const [selectedPacote, setSelectedPacote] = useState(null);
    const itemsPerPage = 10;

    const { accountId } = useMe();

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: itemsPerPage,
        ...activeFilters
    }), [currentPage, activeFilters]);

    const { data: pacotesData, isLoading, refetch } = usePacotesCliente(queryParams);
    const { data: clientesData, isLoading: loadingClientes } = useClientes({ limit: 1000 }, accountId);
    const { updatePacote, deletePacote } = useMutatePacotesCliente();

    const clientes = clientesData?.data || [];
    const pacotes = pacotesData?.data || [];
    const count = pacotesData?.totalCount || pacotesData?.count || 0;
    const pagination = pacotesData?.pagination || {};

    const handleAdicionarClick = () => {
        setPacoteEmEdicao(null);
        setShowForm(true);
        setShowFilters(false);
    };

    const handleEditClick = (pacote) => {
        setPacoteEmEdicao(pacote);
        setShowForm(true);
        setShowFilters(false);
        setSelectedPacote(null);
    };

    const handleDelete = (pacoteId) => {
        if (window.confirm('Tem certeza que deseja excluir este pacote? Esta ação não pode ser desfeita.')) {
            deletePacote.mutate(pacoteId, {
                onSuccess: () => setSelectedPacote(null),
            });
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setPacoteEmEdicao(null);
    };

    const handleSaveSuccess = () => {
        handleCloseForm();
        refetch();
    };

    const handleFiltrosClick = () => {
        setShowFilters(p => !p);
        if (showForm) {
            setShowForm(false);
            setPacoteEmEdicao(null);
        }
    };

    const isMutating = updatePacote.isPending || deletePacote.isPending;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Archive />}
                    title="Pacotes dos Clientes"
                    subtitle={!showForm ? `${count} pacote(s)` : null}
                    actions={<>
                        {!showForm && (
                            <>
                                <button onClick={handleFiltrosClick} className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}>
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {showForm && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {showForm && (
                    <div className="content-panel">
                        <PacoteClienteForm
                            key={pacoteEmEdicao?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={pacoteEmEdicao}
                        />
                    </div>
                )}

                {showFilters && !showForm && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtrar Pacotes</h3>
                        <FiltroPacotesCliente
                            onApplyFilters={setActiveFilters}
                            loading={isLoading || loadingClientes}
                            clientes={clientes}
                        />
                    </div>
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading && pacotes.length === 0 ? (
                            <div className="table-loading-state"><Loader2 size={24} className="animate-spin"/></div>
                        ) : (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Oferta</th>
                                        <th>Pacote</th>
                                        <th>Uso</th>
                                        <th>Data da Compra</th>
                                        <th>Validade</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pacotes.length > 0 ? (
                                        pacotes.map(p =>
                                            <PacoteClienteRow
                                                key={p._id}
                                                pacote={p}
                                                onOpenActions={setSelectedPacote}
                                                loading={isMutating}
                                            />
                                        )
                                    ) : (
                                        <tr><td colSpan="8" className="td-empty">Nenhum pacote vendido encontrado.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
            {!showForm && pagination && count > itemsPerPage && !isLoading && <Pagination {...pagination} totalItems={count} onPageChange={setCurrentPage} currentPage={currentPage} />}

            <FabButton onClick={handleAdicionarClick} title="Adicionar Pacote" visible={!showForm} disabled={isLoading} icon={<Archive size={26} className="icon"/>}/>

            <ActionsDrawer
                open={!!selectedPacote}
                onClose={() => setSelectedPacote(null)}
                title={selectedPacote?.pacoteComprado?.nome || 'Pacote'}
                subtitle={selectedPacote?.cliente?.user?.name}
                actions={selectedPacote ? [
                    {
                        key: 'editar',
                        label: 'Editar Status e Observações',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedPacote),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedPacote._id),
                        destructive: true,
                        loading: isMutating,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default PacotesCliente;
